import {FC} from 'react'

import {Text, TextVariant} from '@/components/common/content/Text'
import {getQuestionsAndAnswersContent} from '@/content/questionsAndAnswers'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {
  QuestionsAndAnswersContentType,
  questionsAndAnswersContentTypeToRoute,
} from '@/utils/questions-and-answers/constants'
import {QuestionsAndAnswersRelatedQuestion} from '@/utils/questions-and-answers/types'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'
import {AnalyticsTrackedEvent} from '@/utils/tracking/types'

import {Button} from '../common/content/Button'
import {ArrowIcon} from '../common/icons/ArrowIcon'

type QuestionsAndAnswersRelatedQuestionsProps = {
  type: QuestionsAndAnswersContentType
  title?: string
  relatedQuestions: QuestionsAndAnswersRelatedQuestion[]
  buttonLinkHref?: string
  buttonLinkText?: string
  trackedEvent?: AnalyticsTrackedEvent
}

export const QuestionsAndAnswersRelatedQuestions: FC<QuestionsAndAnswersRelatedQuestionsProps> = ({
  type,
  title,
  relatedQuestions,
  buttonLinkHref,
  buttonLinkText,
  trackedEvent,
}) => {
  const {t} = useTranslation()
  const {relatedQuestionsTitle} = getQuestionsAndAnswersContent(type)

  return (
    <>
      <div>
        <div className="title">
          <Text variant={TextVariant.h2} align="center">
            {title ?? t(relatedQuestionsTitle)}
          </Text>
        </div>
        <ul className="list">
          {relatedQuestions.map(({name, slug, categorySlug}) => (
            <li key={slug} className="item">
              <Text>
                <Link
                  href={questionsAndAnswersContentTypeToRoute[type].question(categorySlug, slug)}
                  legacyBehavior
                >
                  <a className="underline">{name}</a>
                </Link>
              </Text>
            </li>
          ))}
        </ul>
        {buttonLinkHref && buttonLinkText && (
          <div className="button-wrapper">
            <Link
              href={buttonLinkHref}
              onClick={
                trackedEvent
                  ? () => analytics.track(trackedEvent.name, trackedEvent.parameters)
                  : undefined
              }
            >
              <Button type="button" icon={<ArrowIcon />} isHoverable>
                {buttonLinkText}
              </Button>
            </Link>
          </div>
        )}
      </div>

      <style jsx>{`
        .title {
          max-width: 800px;
          margin: 0 auto;
        }
        .list {
          column-count: 2;
          margin: ${theme.spacing[8]} -${theme.spacing[4]} -${theme.spacing[3]};
        }
        .item {
          position: relative;
          margin: 0 ${theme.spacing[4]} ${theme.spacing[3]};
          display: table;
          padding-left: ${theme.spacing[3]};
        }
        .item::before {
          content: '';
          width: 5px;
          height: 5px;
          position: absolute;
          left: 0;
          top: 10px;
          background-color: ${theme.palette.text.default};
          border-radius: 50%;
        }
        .button-wrapper {
          margin-top: ${theme.spacing[8]};
          text-align: center;
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .list {
            column-count: inherit;
            margin-top: ${theme.spacing[6]};
          }
          .button-wrapper {
            margin-top: ${theme.spacing[7]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .list {
            margin: ${theme.spacing[6]} 0;
          }
          .item {
            margin: 0 ${theme.spacing[2]} ${theme.spacing[3]};
          }
          .button-wrapper {
            margin-top: ${theme.spacing[6]};
          }
        }
      `}</style>
    </>
  )
}
